import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTelegram } from '../contexts/TelegramContext';
import { useAuthStore } from '../stores/authStore';
import { useSearchParams } from 'react-router-dom';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { getPersistedBrowserE2eToken, persistBrowserE2eToken } from '@/lib/browserE2eAuth';

function safeTelegramAlert(webApp: { showAlert?: (m: string) => void } | null, message: string) {
  if (!webApp?.showAlert) return;
  try {
    webApp.showAlert(message);
  } catch {
    // Вне Telegram (в т.ч. встроенный браузер / MCP) showAlert может кидать WebAppMethodUnsupported
  }
}

export const LoginPage: React.FC = () => {
  const { initData, webApp, isBrowserMode } = useTelegram();
  const { authenticate, authenticateByUsername, isAuthenticated, loading, error } = useAuthStore();
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();
  const browserE2eTokenFromUrl = useMemo(() => {
    const raw = searchParams.get('browser_e2e_token');
    return raw != null && raw.trim().length > 0 ? raw.trim() : '';
  }, [searchParams]);

  useEffect(() => {
    if (browserE2eTokenFromUrl) {
      persistBrowserE2eToken(browserE2eTokenFromUrl);
    }
  }, [browserE2eTokenFromUrl]);

  /** URL-параметр или значение из sessionStorage после редиректа с /login?browser_e2e_token=… */
  const browserE2eAuthToken =
    browserE2eTokenFromUrl || getPersistedBrowserE2eToken();
  const allowBrowserUsernameAuth = browserE2eAuthToken.length > 0;

  const [username, setUsername] = useState('');
  const [showBrowserForm, setShowBrowserForm] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [telegramBlockedMessage, setTelegramBlockedMessage] = useState<string | null>(null);

  const showUsernameForm = allowBrowserUsernameAuth && (isBrowserMode || showBrowserForm);

  // initData может подъехать с задержкой (ретраи в TelegramContext). Снимаем ошибку,
  // чтобы авторизация получила шанс, а не залипала на экране «не удалось получить данные».
  useEffect(() => {
    if (initData) {
      setTelegramBlockedMessage(null);
    }
  }, [initData]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
      return;
    }

    if (isBrowserMode) {
      if (allowBrowserUsernameAuth) {
        setShowBrowserForm(true);
        setTelegramBlockedMessage(null);
      } else {
        setShowBrowserForm(false);
        setTelegramBlockedMessage(null);
      }
      return;
    }

    if (initData && !loading && !showBrowserForm) {
      console.log('Attempting authentication with initData:', initData ? `${initData.substring(0, 100)}...` : 'empty');
      authenticate(initData).catch(() => {
        console.error('Authentication error');
        if (allowBrowserUsernameAuth) {
          setShowBrowserForm(true);
          safeTelegramAlert(webApp, 'Авторизация через Telegram не удалась. Используйте форму ниже.');
        } else {
          setTelegramBlockedMessage(
            'Авторизация через Telegram не удалась. Откройте мини-приложение снова из Telegram.',
          );
          safeTelegramAlert(webApp, 'Авторизация через Telegram не удалась.');
        }
      });
    } else if (!initData && !loading && !isBrowserMode && !showBrowserForm && !telegramBlockedMessage) {
      console.warn('No initData available from Telegram WebApp');
      if (allowBrowserUsernameAuth) {
        setShowBrowserForm(true);
        safeTelegramAlert(
          webApp,
          'Не удалось получить данные авторизации из Telegram. Используйте форму ниже.',
        );
      } else {
        setTelegramBlockedMessage(
          'Не удалось получить данные авторизации из Telegram. Откройте приложение из Telegram.',
        );
        safeTelegramAlert(webApp, 'Не удалось получить данные авторизации из Telegram.');
      }
    }
  }, [
    initData,
    isAuthenticated,
    loading,
    authenticate,
    navigate,
    webApp,
    isBrowserMode,
    showBrowserForm,
    allowBrowserUsernameAuth,
    telegramBlockedMessage,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUsernameError(null);

    if (!browserE2eAuthToken) {
      setUsernameError('Отсутствует токен браузерной авторизации');
      return;
    }

    if (!username.trim()) {
      setUsernameError('Введите ник');
      return;
    }

    if (username.length < 3) {
      setUsernameError('Ник должен содержать минимум 3 символа');
      return;
    }

    if (username.length > 50) {
      setUsernameError('Ник не может быть длиннее 50 символов');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setUsernameError('Ник может содержать только буквы, цифры и подчеркивания');
      return;
    }

    try {
      await authenticateByUsername(username.trim(), browserE2eAuthToken);
    } catch (err: any) {
      setUsernameError(err.message || 'Ошибка авторизации');
    }
  };

  if (isBrowserMode && !allowBrowserUsernameAuth) {
    return (
      <div className="container max-w-md mx-auto px-4" data-testid="login-page">
        <div className="flex flex-col items-center justify-center min-h-screen py-6">
          <Card className="w-full max-w-md" data-testid="login-browser-telegram-only">
            <CardHeader>
              <CardTitle className="text-center text-2xl">Вход через Telegram</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground text-center">
                Откройте это приложение в Telegram (мини-приложение), чтобы войти.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (telegramBlockedMessage) {
    return (
      <div className="container max-w-md mx-auto px-4" data-testid="login-page">
        <div className="flex flex-col items-center justify-center min-h-screen py-6">
          <Card className="w-full max-w-md" data-testid="login-telegram-auth-failed">
            <CardHeader>
              <CardTitle className="text-center text-2xl">Не удалось войти</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground text-center">{telegramBlockedMessage}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showUsernameForm) {
    return (
      <div className="container max-w-md mx-auto px-4" data-testid="login-page">
        <div className="flex flex-col items-center justify-center min-h-screen py-6">
          <Card className="w-full max-w-md" data-testid="login-browser-form">
            <CardHeader>
              <CardTitle className="text-center text-2xl">
                Вход / Регистрация
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <p
                  className="text-sm text-muted-foreground text-center"
                  data-testid="login-help-hint"
                >
                  Введите ваш ник для входа. Если вы новый пользователь, будет создан аккаунт.
                </p>

                {error && (
                  <Alert variant="destructive" data-testid="login-global-error">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {usernameError && (
                  <Alert variant="destructive" data-testid="login-username-error">
                    <AlertDescription className="flex items-center justify-between">
                      <span>{usernameError}</span>
                      <button
                        type="button"
                        onClick={() => setUsernameError(null)}
                        className="alert-dismiss-btn"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleUsernameSubmit}>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="username">
                        Ник
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        id="username"
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                          setUsernameError(null);
                        }}
                        disabled={loading}
                        required
                        placeholder="Введите ваш ник"
                        className={usernameError ? 'border-destructive' : ''}
                        data-testid="login-username-input"
                      />
                      <p
                        className="text-xs text-muted-foreground"
                        data-testid="login-username-rules-hint"
                      >
                        Минимум 3 символа, только буквы, цифры и подчеркивания
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full"
                      size="lg"
                      data-testid="login-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Вход...
                        </>
                      ) : (
                        'Войти / Зарегистрироваться'
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div
      className="app-screen flex flex-col items-center justify-center p-6"
      data-testid="login-telegram-auth-loading"
    >
      <Card className="w-full max-w-sm border-border/40 shadow-card-hover">
        <CardContent className="flex flex-col items-center gap-6 py-10 px-6">
          <div className="relative flex h-16 w-16 items-center justify-center">
            <span
              className="absolute inset-0 rounded-full bg-primary/15 animate-ping opacity-40"
              style={{ animationDuration: '2s' }}
              aria-hidden
            />
            <span className="absolute inset-2 rounded-full bg-primary/20" aria-hidden />
            <Loader2
              className="relative h-7 w-7 animate-spin text-primary"
              aria-hidden
            />
          </div>
          <div className="text-center space-y-2">
            <p className="text-lg font-semibold text-foreground tracking-tight">
              Подключаемся к Telegram
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Проверяем данные и готовим ваш дневник. Это займёт несколько секунд.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
